/**
 * שילוח ספקים — the goods a supplier ships to us, from the factory gate to our warehouse.
 *
 * ## Where this sits
 *
 * הזמנות ספקים (`productionOrder.types.ts`) ends at `ready_to_ship`: the factory has made the goods.
 * A shipment is what moves them — by air, by sea, or by truck from a supplier in Israel — and one
 * order may leave in several shipments while one shipment may carry several orders, including
 * orders of different suppliers consolidated into one container. So a shipment has its own lines,
 * each pointing at a `ProductionOrderItem`, and "shipped
 * so far" is a sum over those lines and never a column on the order (`docs/production-orders/PROGRESS.md`).
 *
 * ## A supplier is not a carrier
 *
 * The supplier is where the goods come from; the carrier (MSC, Maersk, DHL) is who moves them.
 * Two separate fields and two separate tables — ADR-005 says the same for outbound shipping.
 *
 * ## The container is a string, not a table
 *
 * Several sea shipments may share one container (a consolidated load). The container has no life
 * of its own beyond its number and type, so the shipments carry both and the containers view groups
 * by the number. A table would need its own screen to keep in step with the bill of lading for no
 * fact the shipments do not already hold.
 *
 * No React, no MUI, no browser globals — the backend imports this file directly.
 */

import { PhOption, phOptions } from './productHub.core';

export * from './supplierShipment.dtos';
export * from './supplierShipment.documents';
export * from './supplierShipment.selection';
export * from './supplierShipment.link';

/** What the table, the form and the Excel header call the arrival date. The column stays `eta`. */
export const EXPECTED_ARRIVAL_LABEL = 'תאריך הגעה צפוי';

/** Headers a sheet may still use for that column. The template now writes `EXPECTED_ARRIVAL_LABEL`. */
export const LEGACY_ARRIVAL_HEADERS = ['ETA', 'ETA — הגעה משוערת'] as const;

/* ------------------------------------------------------------------ */
/* Status                                                              */
/* ------------------------------------------------------------------ */

/** The six steps in order, plus `cancelled` outside them. Stable English codes, Hebrew labels. */
export const SUPPLIER_SHIPMENT_FLOW = [
  'draft',
  'ready_for_pickup',
  'left_supplier',
  'in_transit',
  'arrived',
  'received'
] as const;

export const SUPPLIER_SHIPMENT_STATUSES = [...SUPPLIER_SHIPMENT_FLOW, 'cancelled'] as const;
export type SupplierShipmentStatus = (typeof SUPPLIER_SHIPMENT_STATUSES)[number];

export const SUPPLIER_SHIPMENT_STATUS_LABELS: Record<SupplierShipmentStatus, string> = {
  draft: 'טיוטה',
  ready_for_pickup: 'מוכן לאיסוף',
  left_supplier: 'יצא מהספק',
  in_transit: 'בדרך',
  arrived: 'הגיע ליעד',
  received: 'נקלט',
  cancelled: 'בוטל'
};

export const SUPPLIER_SHIPMENT_STATUS_OPTIONS: PhOption<SupplierShipmentStatus>[] = phOptions(
  SUPPLIER_SHIPMENT_STATUSES,
  SUPPLIER_SHIPMENT_STATUS_LABELS
);

/** Out of the supplier's hands: what moves the order to `shipped` once every unit is on its way. */
export const SUPPLIER_SHIPMENT_DEPARTED: readonly SupplierShipmentStatus[] = [
  'left_supplier',
  'in_transit',
  'arrived',
  'received'
];

/** Somewhere between the supplier and our shelf — what "פעיל" means on every view. */
export const SUPPLIER_SHIPMENT_ACTIVE: readonly SupplierShipmentStatus[] = [
  'ready_for_pickup',
  'left_supplier',
  'in_transit',
  'arrived'
];

/** Physically moving: counted as "בדרך" in the header and "בים" on the containers view. */
export const SUPPLIER_SHIPMENT_MOVING: readonly SupplierShipmentStatus[] = ['left_supplier', 'in_transit'];

export const isShipmentDeparted = (status: SupplierShipmentStatus): boolean =>
  SUPPLIER_SHIPMENT_DEPARTED.includes(status);

/**
 * A draft may be half-filled — an imported row has an order and a date and nothing else. Every
 * other live status claims the goods exist, so it needs lines and a carrier.
 */
export const shipmentNeedsContents = (status: SupplierShipmentStatus): boolean =>
  status !== 'draft' && status !== 'cancelled';

/** Only a draft is deleted; anything a forwarder has been told about is cancelled instead. */
export const isShipmentDeletable = (status: SupplierShipmentStatus): boolean => status === 'draft';

/* ------------------------------------------------------------------ */
/* Method and carrier                                                  */
/* ------------------------------------------------------------------ */

export const SUPPLIER_SHIPMENT_METHODS = ['air', 'sea', 'internal', 'local'] as const;
export type SupplierShipmentMethod = (typeof SUPPLIER_SHIPMENT_METHODS)[number];

export const SUPPLIER_SHIPMENT_METHOD_LABELS: Record<SupplierShipmentMethod, string> = {
  air: 'אווירי',
  sea: 'ימי',
  internal: 'משלוח פנימי',
  local: 'משלוח מספק בארץ'
};

export const SUPPLIER_SHIPMENT_METHOD_OPTIONS = phOptions(SUPPLIER_SHIPMENT_METHODS, SUPPLIER_SHIPMENT_METHOD_LABELS);

/** What the tracking number is called for each method — an AWB flies, a BOL sails. */
export const TRACKING_LABELS: Record<SupplierShipmentMethod, string> = {
  air: 'מספר AWB',
  sea: 'מספר BOL',
  internal: 'מספר מעקב',
  local: 'מספר מעקב'
};

/** A carrier's line of work. `local` covers both domestic methods. */
export const SUPPLIER_CARRIER_MODES = ['air', 'sea', 'local'] as const;
export type SupplierCarrierMode = (typeof SUPPLIER_CARRIER_MODES)[number];

export const SUPPLIER_CARRIER_MODE_LABELS: Record<SupplierCarrierMode, string> = {
  air: 'אווירי',
  sea: 'ימי',
  local: 'מקומי'
};

export const SUPPLIER_CARRIER_MODE_OPTIONS = phOptions(SUPPLIER_CARRIER_MODES, SUPPLIER_CARRIER_MODE_LABELS);

/* ------------------------------------------------------------------ */
/* Packing, containers and cost                                        */
/* ------------------------------------------------------------------ */

export const SHIPMENT_WEIGHT_UNITS = ['kg', 'lb'] as const;
export type ShipmentWeightUnit = (typeof SHIPMENT_WEIGHT_UNITS)[number];
export const SHIPMENT_WEIGHT_UNIT_LABELS: Record<ShipmentWeightUnit, string> = { kg: 'ק״ג', lb: 'ליברות (lb)' };

export const CARTON_UNITS = ['cm', 'in'] as const;
export type CartonUnit = (typeof CARTON_UNITS)[number];
export const CARTON_UNIT_LABELS: Record<CartonUnit, string> = { cm: 'ס״מ', in: 'אינץ׳' };

/** Cubic unit → m³. */
const CBM_FACTOR: Record<CartonUnit, number> = { cm: 1e-6, in: 1.6387e-5 };

export const CONTAINER_TYPES = ['20GP', '40GP', '40HC'] as const;
export type ContainerType = (typeof CONTAINER_TYPES)[number];

/** Usable volume, the figure forwarders quote. */
export const CONTAINER_CAPACITY_CBM: Record<ContainerType, number> = { '20GP': 33, '40GP': 67, '40HC': 76 };

export const CONTAINER_TYPE_OPTIONS: PhOption<ContainerType>[] = [
  { value: '20GP', label: "20' GP — 33 CBM" },
  { value: '40GP', label: "40' GP — 67 CBM" },
  { value: '40HC', label: "40' HC — 76 CBM" }
];

export const SHIPMENT_CURRENCIES = ['USD', 'ILS'] as const;
export type ShipmentCurrency = (typeof SHIPMENT_CURRENCIES)[number];
export const SHIPMENT_CURRENCY_LABELS: Record<ShipmentCurrency, string> = { USD: 'דולר ($)', ILS: 'שקל (₪)' };
export const SHIPMENT_CURRENCY_SYMBOLS: Record<ShipmentCurrency, string> = { USD: '$', ILS: '₪' };

const num = (value: unknown): number => {
  const parsed = parseFloat(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

export interface CartonTypeLike {
  length: number | string | null;
  width: number | string | null;
  height: number | string | null;
  unit: CartonUnit;
  quantity: number | string | null;
}

export const cartonTypeCbm = (carton: CartonTypeLike): number =>
  num(carton.length) * num(carton.width) * num(carton.height) * num(carton.quantity) * CBM_FACTOR[carton.unit];

/**
 * The shipment's volume. Carton types win when they add up to anything, because they are measured;
 * the typed-in CBM is the fallback for a supplier who sent a total and no packing list.
 */
export const shipmentCbm = (cartons: CartonTypeLike[], typedCbm: unknown): number => {
  const measured = cartons.reduce((total, carton) => total + cartonTypeCbm(carton), 0);
  return measured > 0 ? measured : num(typedCbm);
};

export const cartonCount = (cartons: CartonTypeLike[]): number =>
  cartons.reduce((total, carton) => total + num(carton.quantity), 0);

export interface ShipmentCostInput {
  method: SupplierShipmentMethod;
  price: unknown;
  containerCost: unknown;
  containerCbm: unknown;
  cbm: number;
}

/**
 * What the shipment cost us.
 *
 * Sea freight is billed per container, so a shipment pays the container's price in proportion to
 * the volume it takes — `containerCost × (cbm / containerCbm)`. Everything else is billed per
 * shipment and the price is typed in. Zero means "not known yet", and the screens print "—".
 */
export const shipmentCost = ({ method, price, containerCost, containerCbm, cbm }: ShipmentCostInput): number => {
  if (method !== 'sea') return num(price);
  const cost = num(containerCost);
  const volume = num(containerCbm);
  return cost && volume ? cost * (cbm / volume) : 0;
};

/* ------------------------------------------------------------------ */
/* Dates                                                               */
/* ------------------------------------------------------------------ */

/** `YYYY-MM-DD` → whole days from `today` (negative = past). Null for no date. */
export const daysFrom = (date: string | null, today: string): number | null => {
  if (!date) return null;
  const toUtc = (value: string) => {
    const [y, m, d] = value.slice(0, 10).split('-').map(Number);
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((toUtc(date) - toUtc(today)) / 864e5);
};

/** Late = an ETA in the past on a shipment that has not reached us. */
export const isShipmentLate = (status: SupplierShipmentStatus, eta: string | null, today: string): boolean => {
  const days = daysFrom(eta, today);
  return days !== null && days < 0 && !['arrived', 'received', 'cancelled'].includes(status);
};

/* ------------------------------------------------------------------ */
/* Views                                                               */
/* ------------------------------------------------------------------ */

/** "לפי נושא" groups by a planning-project attribute of the order each line belongs to. */
export const SHIPMENT_TOPIC_DIMENSIONS = ['client', 'category', 'tag'] as const;
export type ShipmentTopicDimension = (typeof SHIPMENT_TOPIC_DIMENSIONS)[number];
export const SHIPMENT_TOPIC_DIMENSION_LABELS: Record<ShipmentTopicDimension, string> = {
  client: 'לקוח',
  category: 'קטגוריה',
  tag: 'תגית'
};

/** The stacked bar on each topic: where its units are, in five bands. */
export const SHIPMENT_MIX_BANDS = ['planned', 'ready', 'moving', 'arrived', 'received'] as const;
export type ShipmentMixBand = (typeof SHIPMENT_MIX_BANDS)[number];
export const SHIPMENT_MIX_LABELS: Record<ShipmentMixBand, string> = {
  planned: 'מתוכנן',
  ready: 'מוכן לאיסוף',
  moving: 'בדרך',
  arrived: 'הגיע ליעד',
  received: 'נקלט'
};

export const mixBandOf = (status: SupplierShipmentStatus): ShipmentMixBand | null => {
  if (status === 'draft') return 'planned';
  if (status === 'ready_for_pickup') return 'ready';
  if (status === 'left_supplier' || status === 'in_transit') return 'moving';
  if (status === 'arrived') return 'arrived';
  if (status === 'received') return 'received';
  return null;
};

/* ------------------------------------------------------------------ */
/* Excel import                                                        */
/* ------------------------------------------------------------------ */

/** The template's columns, in order. One row per shipment; every row becomes a draft. */
export const SHIPMENT_IMPORT_COLUMNS = [
  { key: 'orderCode', header: 'מספר PO' },
  { key: 'supplierName', header: 'ספק' },
  { key: 'method', header: 'שיטת שילוח' },
  { key: 'carrierName', header: 'חברת שילוח' },
  { key: 'trackingNumber', header: 'מספר מעקב' },
  { key: 'warehouseName', header: 'מחסן יעד' },
  { key: 'plannedDepartureDate', header: 'תאריך יציאה' },
  { key: 'eta', header: 'תאריך הגעה צפוי' },
  { key: 'cartons', header: 'קרטונים' },
  { key: 'weight', header: 'משקל (ק״ג)' }
] as const;
export type ShipmentImportColumnKey = (typeof SHIPMENT_IMPORT_COLUMNS)[number]['key'];

/* ------------------------------------------------------------------ */
/* Errors                                                              */
/* ------------------------------------------------------------------ */

export const SUPPLIER_SHIPMENT_ERROR_CODES = {
  SHIPMENT_NOT_FOUND: 'SHP_NOT_FOUND',
  SUPPLIER_NOT_FOUND: 'SHP_SUPPLIER_NOT_FOUND',
  ORDER_NOT_OF_SUPPLIER: 'SHP_ORDER_NOT_OF_SUPPLIER',
  ITEM_NOT_IN_ORDERS: 'SHP_ITEM_NOT_IN_ORDERS',
  OVER_REMAINING: 'SHP_OVER_REMAINING',
  INCOMPLETE: 'SHP_INCOMPLETE',
  WAREHOUSE_NOT_FOUND: 'SHP_WAREHOUSE_NOT_FOUND',
  CARRIER_NOT_FOUND: 'SHP_CARRIER_NOT_FOUND',
  LOCKED_RECEIVED: 'SHP_LOCKED_RECEIVED',
  NOT_DELETABLE: 'SHP_NOT_DELETABLE',
  FILE_NOT_FOUND: 'SHP_FILE_NOT_FOUND',
  CARRIER_DUPLICATE: 'SHP_CARRIER_DUPLICATE',
  IMPORT_FORMAT: 'SHP_IMPORT_FORMAT',
  SUPPLIER_NO_EMAIL: 'SHP_SUPPLIER_NO_EMAIL',
  LINK_SEND_FAILED: 'SHP_LINK_SEND_FAILED'
} as const;
